import { Link } from "react-router-dom";
import type { AttentionItem } from "../../lib/dashboard";
import { StatusMark } from "../ui/Status";
import { MicroLabel, SectionTitle } from "../ui/Text";

/**
 * What somebody has to act on, most urgent first, each row a link to the page
 * where it is fixed.
 *
 * The list is the reason the dashboard is the landing page: on a good day it is
 * one quiet line, and on a bad day it is the first thing read. Composed client-side
 * from the panels' own data (`lib/dashboard.ts`), so it never says more than the
 * principal may see.
 */
export function AttentionList({
  items,
  isComplete,
  hasFailure,
}: {
  readonly items: readonly AttentionItem[];
  /**
   * Every source this principal may read has loaded. Until then an empty list is
   * "not known yet", and printing the all-clear over it would be the one lie this
   * panel must never tell.
   */
  readonly isComplete: boolean;
  /**
   * A source failed. The list then says so, rather than sitting on "Checking"
   * for good — the failed panel shows the error, and this line says the list is
   * missing whatever that panel would have added to it.
   */
  readonly hasFailure: boolean;
}) {
  return (
    <section aria-labelledby="attention-title" className="flex flex-col gap-3">
      <SectionTitle>
        <span id="attention-title">Needs attention</span>
      </SectionTitle>

      {hasFailure ? (
        <p role="status" className="m-0 flex items-center gap-2 text-data text-signal-ink">
          <StatusMark tone="faulted" />
          Some figures could not be loaded, so this list may be incomplete.
        </p>
      ) : null}

      {items.length === 0 && hasFailure ? null : items.length === 0 ? (
        <p className="m-0 flex items-center gap-2 py-2 text-body text-ink-muted">
          {isComplete ? (
            <>
              <StatusMark tone="available" />
              Nothing needs attention.
            </>
          ) : (
            <MicroLabel>Checking</MicroLabel>
          )}
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col p-0">
          {items.map((item) => (
            <li key={item.key} className="border-b border-rule last:border-b-0">
              <Link
                to={item.to}
                className={[
                  "group flex items-baseline gap-3 py-2.5 no-underline",
                  "transition-colors duration-fast ease-swiss hover:bg-paper-sunken",
                ].join(" ")}
              >
                <span className="translate-y-[-1px] self-center">
                  <StatusMark tone={item.tone} />
                </span>
                <span className="flex min-w-0 flex-1 flex-col gap-0.5 md:flex-row md:items-baseline md:gap-4">
                  <span className="text-body font-medium text-ink">{item.title}</span>
                  <span className="truncate text-data text-ink-faint">{item.detail}</span>
                </span>
                <span
                  aria-hidden="true"
                  className="text-ink-faint transition-transform duration-fast ease-swiss group-hover:translate-x-0.5 group-hover:text-ink"
                >
                  →
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
